<template>
  <div>
    <v-dialog v-model="creationDialog" max-width="800px" :fullscreen="isMobile">
      <template v-slot:activator="{ on, attrs }">
        <Button v-on="on" v-bind="attrs">
          <v-icon>{{ mdiPlus }}</v-icon>
          <span class="d-none d-md-inline">Eigenes Feld hinzufügen</span>
        </Button>
      </template>
      <v-card class="d-flex flex-column pa-0 pa-md-4">
        <v-card-title
          style="
            display: flex;
            justify-content: space-between;
            flex-wrap: nowrap;
          "
          :style="{
            ...(isMobile ? { padding: '20px 16px 12px 16px' } : {}),
          }"
        >
          <div
            :style="{
              fontSize: isMobile ? '16px' : '20px',
            }"
          >
            Eigenes Feld hinzufügen
          </div>
          <v-icon @click="creationDialog = false">{{ mdiClose }}</v-icon>
        </v-card-title>
        <v-card-text
          style="padding-bottom: 4px"
          :style="{
            ...(isMobile ? { padding: '16px' } : {}),
          }"
        >
          <CustomAttributesForm
            ref="customAttributesForm"
            @commit="handleCreation($event)"
            @dirty="setEnableSave"
          />
          <ErrorText style="margin-bottom: 8px" v-if="creationError">
            {{ creationError }}
          </ErrorText>
        </v-card-text>
        <v-spacer></v-spacer>
        <v-card-actions style="padding-bottom: 16px">
          <v-spacer></v-spacer>
          <TextButton
            @click="cancelForm"
            style="margin-right: 8px"
            label="Abbrechen"
          ></TextButton>
          <Button
            :loading="loading"
            :disabled="!enableSave || loading"
            @click="submitForm"
            >Speichern</Button
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <SuccessSnackbar
      message="Das Feld wurde erfolgreich erstellt."
      v-model="showSuccessSnackbar"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, Ref, ref } from "vue"
import Button from "components/UI/Button.vue"
import CustomAttributesForm from "app/modules/custom-attributes/form/CustomAttributesForm.vue"
import { mdiClose, mdiPlus } from "@mdi/js"
import {
  CustomAttributeDefinition,
  CustomAttributeDefinitionAttributableTypeEnum,
} from "suus-api"
import { useIsMobile } from "features/common/vuetify/useVuetify"
import TextButton from "components/UI/TextButton.vue"
import SuccessSnackbar from "app/ui-primitives/snackbars/SuccessSnackbar.vue"
import ErrorText from "app/ui-primitives/text/ErrorText.vue"
import { CustomAttributesClient } from "app/modules/custom-attributes/client"

export default defineComponent({
  name: "CustomAttributesCreate",
  components: {
    ErrorText,
    SuccessSnackbar,
    TextButton,
    CustomAttributesForm,
    Button,
  },
  props: {
    attributable_type: {
      type: String as PropType<CustomAttributeDefinitionAttributableTypeEnum>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const creationDialog = ref(false)

    const loading = ref(false)
    const creationError: Ref<string | null> = ref(null)

    const handleCreation = async (definition: CustomAttributeDefinition) => {
      loading.value = true
      creationError.value = null
      try {
        const createdAttr = await CustomAttributesClient.create({
          ...definition,
          attributable_type: props.attributable_type,
        })
        customAttributesForm.value?.resetForm()
        emit("created", createdAttr)
        creationDialog.value = false
        showSuccessSnackbar.value = true
      } catch (error) {
        creationError.value =
          "Es gab einen Fehler beim Erstellen des Attributs."
        console.error(error)
      } finally {
        loading.value = false
      }
    }

    const customAttributesForm = ref<InstanceType<
      typeof CustomAttributesForm
    > | null>(null)

    const enableSave = ref(false)
    const setEnableSave = (value: boolean) => {
      enableSave.value = value
    }

    const submitForm = () => {
      customAttributesForm.value?.submitForm()
    }

    const cancelForm = () => {
      customAttributesForm.value?.resetForm()
      creationDialog.value = false
    }

    const isMobile = useIsMobile()

    const showSuccessSnackbar = ref(false)

    return {
      mdiPlus,
      mdiClose,
      creationDialog,
      handleCreation,
      customAttributesForm,
      enableSave,
      setEnableSave,
      submitForm,
      cancelForm,
      isMobile,
      showSuccessSnackbar,
      loading,
      creationError,
    }
  },
})
</script>
